import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RobotCleaningProgressActions } from '../robotCleaningProgressSlice';
import { selectRobotCleaningProgressEntry } from '../robotCleaningProgressSelectors';
import type { RobotCleaningProgressEntry } from '../robotCleaningProgressSlice';
import { Machine } from 'app/modules/machine-inventory/interfaces/Machine.types';
import type { IState } from 'app/cross-cutting-concerns/state-management/interfaces/IState';
import type { Optional } from 'lib/types/Optional';

// Called by sibling progress consumers (route, progress bar, live map card); ref-counted per robotId
// so one unmounting doesn't close another's subscription.
const activeRobotIdRefCounts = new Map<string, number>();

/** Keeps the robot's current-task progress subscription open while `isActive`, and returns its entry. */
export const useRobotCleaningProgressEntry = (
  robot: Pick<Machine, 'id' | 'materialNumber' | 'serialNumber'>,
  isActive: boolean
): Optional<RobotCleaningProgressEntry> => {
  const dispatch = useDispatch();

  const entry = useSelector((state: IState) => selectRobotCleaningProgressEntry(state, robot.id));

  useEffect(() => {
    if (!isActive) {
      return undefined;
    }

    const { id: robotId, materialNumber, serialNumber } = robot;
    const refCount = activeRobotIdRefCounts.get(robotId) ?? 0;
    activeRobotIdRefCounts.set(robotId, refCount + 1);

    if (refCount === 0) {
      dispatch(
        RobotCleaningProgressActions.openRobotCleaningProgressRequest({ robotId, materialNumber, serialNumber })
      );
    }

    return (): void => {
      const remaining = (activeRobotIdRefCounts.get(robotId) ?? 1) - 1;

      if (remaining <= 0) {
        activeRobotIdRefCounts.delete(robotId);
        dispatch(RobotCleaningProgressActions.closeRobotCleaningProgress({ robotId }));
      } else {
        activeRobotIdRefCounts.set(robotId, remaining);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, robot.id]);

  return entry;
};
